/**
 * WS connection retry wrapper for the Tauri desktop app.
 *
 * Wraps the NatsClient singleton with exponential-backoff retry logic.
 * Updates the shared RetryStore so ConnectionBanner can display progress.
 *
 * G5: respects the typed `ConnectError`. Fatal errors stop the retry loop
 * immediately (status='fatal', lastError populated) so the UI can surface
 * an actionable CTA. Transient errors use the existing backoff.
 */

import { type ConnectError, getNatsClient, isFatal } from '@khal-os/sdk/app';
import { useRetryStore, type RetryStatus } from '@khal-os/desktop-shell';

// --- Constants ---
const BACKOFF_SCHEDULE = [1, 2, 4, 8, 16, 30]; // seconds
const MAX_RETRIES = 10;

// --- Internal state ---
type RetryInternalState = 'idle' | 'connecting' | 'connected' | 'retry_waiting' | 'retry_failed' | 'fatal';

let currentState: RetryInternalState = 'idle';
let retryCount = 0;
let lastError: ConnectError | undefined;
let countdownTimer: ReturnType<typeof setInterval> | null = null;
let retryTimeout: ReturnType<typeof setTimeout> | null = null;
let stateListeners: Array<(state: RetryInternalState) => void> = [];
let errorUnsubscribe: (() => void) | null = null;

// --- Helpers ---

function getBackoffSeconds(attempt: number): number {
	const idx = Math.min(attempt, BACKOFF_SCHEDULE.length - 1);
	return BACKOFF_SCHEDULE[idx];
}

function clearTimers() {
	if (countdownTimer !== null) {
		clearInterval(countdownTimer);
		countdownTimer = null;
	}
	if (retryTimeout !== null) {
		clearTimeout(retryTimeout);
		retryTimeout = null;
	}
}

function setState(state: RetryInternalState) {
	currentState = state;
	for (const listener of stateListeners) {
		listener(state);
	}
}

function syncStore(status: RetryStatus, retryIn: number) {
	useRetryStore.setState({
		active: true,
		status,
		retryCount,
		retryIn,
		maxRetries: MAX_RETRIES,
		onManualRetry: manualRetry,
		lastError,
	});
}

/**
 * Fatal-stop: invoked when the transport emits an `isFatal(err)` ConnectError.
 * Cancels pending retries, freezes the store in `status='fatal'`, and keeps
 * the error available so the UI can render the right CTA (sign in / reload
 * / contact admin). The only way out is a `manualRetry()`.
 */
function stopRetryFatal(err: ConnectError): void {
	clearTimers();
	lastError = err;
	setState('fatal');
	useRetryStore.setState({
		active: true,
		status: 'fatal',
		retryCount,
		retryIn: 0,
		maxRetries: MAX_RETRIES,
		onManualRetry: manualRetry,
		lastError: err,
	});
}

/**
 * Attempt a single connection via the NatsClient.
 *
 * The NatsClient.connect() is private in TS but accessible at runtime.
 * We call it to re-trigger the Tauri WS connection flow.
 */
async function attemptConnect(): Promise<boolean> {
	const client = getNatsClient();

	// If already connected, nothing to do
	if (client.connectionState === 'connected') {
		return true;
	}

	setState('connecting');
	syncStore('connecting', 0);

	// Call the internal connect method to re-trigger WS connection. The
	// transport reports success/failure via `onConnectionStateChange(state, err)`
	// — see the subscribe block in connectWithRetry(). We don't swallow the
	// error here: the state listener may flag it fatal and short-circuit.
	const clientAny = client as { connectViaTauri?: () => Promise<void> };
	if (typeof clientAny.connectViaTauri === 'function') {
		await clientAny.connectViaTauri().catch(() => {
			// connectViaTauri translates errors into typed ConnectError via
			// setConnectionState('disconnected', err); listener handles it.
		});
	}

	return (client.connectionState as string) === 'connected';
}

function scheduleRetry(): void {
	// Defence-in-depth: if a fatal error already surfaced, don't schedule a
	// retry. The transport listener should have called stopRetryFatal
	// first — this guard catches any race where attemptConnect() returned
	// false before the state listener fired.
	if (lastError && isFatal(lastError)) {
		stopRetryFatal(lastError);
		return;
	}

	if (retryCount >= MAX_RETRIES) {
		setState('retry_failed');
		syncStore('retry_failed', 0);
		return;
	}

	const delaySec = getBackoffSeconds(retryCount);
	let remaining = delaySec;

	setState('retry_waiting');
	syncStore('retry_waiting', remaining);

	// Countdown timer — tick every second for the UI
	countdownTimer = setInterval(() => {
		remaining -= 1;
		if (remaining >= 0) {
			syncStore('retry_waiting', remaining);
		}
	}, 1000);

	// Actual retry after the backoff delay
	retryTimeout = setTimeout(async () => {
		clearTimers();
		retryCount += 1;

		const success = await attemptConnect();
		if (success) {
			onConnected();
		} else {
			scheduleRetry();
		}
	}, delaySec * 1000);
}

function onConnected() {
	clearTimers();
	setState('connected');
	lastError = undefined;
	useRetryStore.setState({
		active: false,
		status: 'connected',
		retryCount: 0,
		retryIn: 0,
		maxRetries: MAX_RETRIES,
		onManualRetry: null,
		lastError: undefined,
	});
}

// --- Public API ---

/**
 * Attempt WS connection with exponential backoff retry.
 * Returns true if connection succeeds, false if all retries exhausted.
 */
export async function connectWithRetry(): Promise<boolean> {
	// Reset state
	clearTimers();
	retryCount = 0;
	lastError = undefined;

	const client = getNatsClient();

	// Listen for connection state + typed ConnectError from the transport.
	// Fatal errors kill the retry loop immediately. Transient errors are
	// recorded as lastError and flow through the backoff schedule.
	if (errorUnsubscribe) errorUnsubscribe();
	errorUnsubscribe = client.onConnectionStateChange((newState, err) => {
		if (newState === 'connected' && currentState !== 'connected') {
			onConnected();
			return;
		}
		if (err) {
			lastError = err;
			if (isFatal(err)) {
				stopRetryFatal(err);
			} else {
				// Transient: keep retrying, but expose lastError so the UI
				// can render "reconnecting — last error: <kind>".
				useRetryStore.setState({ lastError: err });
			}
		}
	});

	// First attempt — the NatsClient constructor already tried once.
	// Check if we're already connected.
	if (client.connectionState === 'connected') {
		onConnected();
		return true;
	}

	// The initial connection failed — start retry loop
	syncStore('connecting', 0);
	scheduleRetry();

	// Return a promise that resolves when connected or all retries fail / fatal
	return new Promise<boolean>((resolve) => {
		const listener = (state: RetryInternalState) => {
			if (state === 'connected') {
				stateListeners = stateListeners.filter((l) => l !== listener);
				resolve(true);
			} else if (state === 'retry_failed' || state === 'fatal') {
				stateListeners = stateListeners.filter((l) => l !== listener);
				resolve(false);
			}
		};
		stateListeners.push(listener);
	});
}

/**
 * Reset retry counter and attempt connection immediately.
 * Can be called from UI "Retry" buttons.
 */
export async function manualRetry(): Promise<boolean> {
	clearTimers();
	retryCount = 0;
	lastError = undefined;
	setState('connecting');
	syncStore('connecting', 0);

	const success = await attemptConnect();
	if (success) {
		onConnected();
		return true;
	}

	// Start retry schedule from zero. If the attempt surfaced a fatal error,
	// scheduleRetry short-circuits via its own guard.
	scheduleRetry();

	return new Promise<boolean>((resolve) => {
		const listener = (state: RetryInternalState) => {
			if (state === 'connected') {
				stateListeners = stateListeners.filter((l) => l !== listener);
				resolve(true);
			} else if (state === 'retry_failed' || state === 'fatal') {
				stateListeners = stateListeners.filter((l) => l !== listener);
				resolve(false);
			}
		};
		stateListeners.push(listener);
	});
}

/**
 * Register a listener for retry state changes.
 * Returns an unsubscribe function.
 */
export function onRetryStateChange(listener: (state: RetryInternalState) => void): () => void {
	stateListeners.push(listener);
	return () => {
		stateListeners = stateListeners.filter((l) => l !== listener);
	};
}

/**
 * Get the current retry state snapshot.
 */
export function getRetryState() {
	return {
		state: currentState,
		retryCount,
		maxRetries: MAX_RETRIES,
		lastError,
	};
}

/** Test-only: reset the singleton retry state between cases. */
export function _resetRetryStateForTests(): void {
	clearTimers();
	if (errorUnsubscribe) {
		errorUnsubscribe();
		errorUnsubscribe = null;
	}
	currentState = 'idle';
	retryCount = 0;
	lastError = undefined;
	stateListeners = [];
	useRetryStore.setState({
		active: false,
		status: 'idle',
		retryCount: 0,
		retryIn: 0,
		maxRetries: MAX_RETRIES,
		onManualRetry: null,
		lastError: undefined,
	});
}
